import store from '@/store'
import { KeyDict } from '@/types'
import { computed } from '@vue/composition-api'
import useRelease from './release'

enum ServiceState {
  UNKNOWN = 'unknown',
  RUNNING = 'running',
  OFFLINE = 'offline'
}

interface ParsedServiceInfo {
  time: string,
  state: ServiceState,
  tooltip: string
}

const useCurrentRelease = (version: string) => {
  const { release } = useRelease()

  const currentReleaseVersion = computed(() => {
    return release.value?.versions[version] ?? null
  })

  const currentReleaseId = computed(() => {
    if (currentReleaseVersion.value === null) return 'unknown'
    return currentReleaseVersion.value.id
  })

  const currentReleasefrontendBranch = computed(() => {
    if (currentReleaseVersion.value === null) return 'unknown'
    return currentReleaseVersion.value.branch_front
  })

  const currentReleaseBackendBranch = computed(() => {
    if (currentReleaseVersion.value === null) return 'unknown'
    return currentReleaseVersion.value.branch_back
  })

  const currentReleaseBackendMachine = computed(() => {
    if (currentReleaseVersion.value === null) return 'unknown'
    return currentReleaseVersion.value.machine_back ?? 'no machine running'
  })

  const currentReleaseHasFirestoreRules = computed(() => {
    if (currentReleaseVersion.value === null) return false
    return currentReleaseVersion.value.deploy_firebase?.rules ?? false
  })

  const currentReleaseHasFirestoreFunctions = computed(() => {
    if (currentReleaseVersion.value === null) return false
    return currentReleaseVersion.value.deploy_firebase?.functions ?? false
  })

  const currentReleaseHasManifests = computed(() => {
    if (currentReleaseVersion.value === null) return false
    return currentReleaseVersion.value.deploy_firebase?.manifests ?? false
  })


  const currentReleaseHasTrialAccounts = computed(() => {
    if (currentReleaseVersion.value === null) return false
    return currentReleaseVersion.value.id === store.state.admin.release?.trialVersion
  })

  const currentReleaseBackendCommit = computed(() => {
    if (currentReleaseVersion.value === null) return 'unknown commit'
    return currentReleaseVersion.value.commit_back ?? 'unknown commit'
  })

  const currentReleaseBackendDeployTime = computed(() => {
    if (currentReleaseVersion.value === null) return 'unknown'
    return currentReleaseVersion.value.deployment_date_back?.toDate().toDateString() ?? 'unknown'
  })

  const currentReleaseServicesEnabled = computed(() => {
    if (currentReleaseVersion.value === null) return {}
    const services = currentReleaseVersion.value.services_enabled ?? {}
    if (release.value) {
      Object.values(release.value.versions).forEach((version) => {
        Object.keys(version.services_enabled).forEach((key) => {
          if (services[key] === undefined) services[key] = false
        })
      })
    }
    return services
  })

  const currentReleaseNgcvpVersion = computed(() => {
    if (currentReleaseVersion.value === null) return 'no-version'
    return currentReleaseVersion.value.ngcvp_version ?? 'no-version'
  })

  const currentReleaseServiceState = computed<KeyDict<ParsedServiceInfo>>(() => {
    if (currentReleaseVersion.value === null) return {}
    const states: KeyDict<ParsedServiceInfo> = {}
    Object.keys(currentReleaseServicesEnabled.value).forEach((serviceid) => {
      if (currentReleaseVersion.value === null) return
      let state = ServiceState.UNKNOWN
      let time = 'unknown'
      const serviceInfo = currentReleaseVersion.value.services_info
      const service = serviceInfo ? serviceInfo[serviceid] : undefined
      if (service !== undefined) {
        state = service.running ? ServiceState.RUNNING : ServiceState.OFFLINE
        time = service?.last_changed_timestamp?.toDate().toDateString() ?? 'unknown'
      }
      const tooltip = `Wanted state is ${currentReleaseServicesEnabled.value[serviceid]}, ` +
                      `real state is ${state} (last update time: ${time})`
      states[serviceid] = {
        state,
        time,
        tooltip
      }
    })
    return states
  })

  return {
    currentReleaseVersion,
    currentReleaseId,
    currentReleasefrontendBranch,
    currentReleaseBackendBranch,
    currentReleaseBackendMachine,
    currentReleaseHasFirestoreRules,
    currentReleaseHasFirestoreFunctions,
    currentReleaseHasManifests,
    currentReleaseHasTrialAccounts,
    currentReleaseBackendCommit,
    currentReleaseBackendDeployTime,
    currentReleaseServicesEnabled,
    currentReleaseNgcvpVersion,
    currentReleaseServiceState
  }
}

export default useCurrentRelease
